"use client";

/**
 * P17-10 · "Pay in 3" under a price.
 *
 * One component for both surfaces the ticket names — the PDP price and the
 * cart total — because they are the same promise about two different amounts,
 * and a shop whose product page offers instalments and whose cart does not is
 * a shop that has changed its mind on the way to the checkout.
 *
 * **Everything a shop without BNPL would pay for is behind the flag.** This
 * module holds only the two checks that need no data — the flag, read from the
 * client context, and whether this amount in this currency could carry a
 * message at all — and the panel that asks the server and mounts Stripe.js is
 * a dynamic import beyond them. `PriceBlock` renders on every product page of
 * every brand and this flag is off by default; a default-off feature must not
 * cost the default shop a network round trip or a payments SDK.
 *
 * The flag is read *optionally*: the buy box and the cart summary are rendered
 * by jsdom tests and by the kitchen sink with no provider, and "there is no
 * brand here" reads as off rather than as a crash on a price the test was
 * actually checking.
 */
import dynamic from "next/dynamic";

import { useOptionalFeatureFlag } from "~/components/feature-flags/provider";
import { isBnplMessagingCurrency } from "~/lib/payments/bnpl";

const BnplPanel = dynamic(
  () => import("~/components/payments/bnpl-panel").then((m) => m.BnplPanel),
  { ssr: false },
);

export function BnplMessage({
  amountMinor,
  currency,
  className,
}: {
  /** The amount the message is about, in minor units. Null → nothing to say. */
  amountMinor: number | null;
  currency: string;
  className?: string;
}) {
  const enabled = useOptionalFeatureFlag("bnpl");
  const code = currency.toUpperCase();

  // A zero or negative amount is refused because "Pay in 3 interest-free
  // payments of £0.00" is the kind of copy that ends up in a screenshot.
  if (!enabled || amountMinor === null || amountMinor <= 0) return null;
  // Stripe's element throws on a currency it does not support, so a shop
  // trading outside its list shows nothing — which is also the right answer,
  // since the method would not be offered at that checkout either.
  if (!isBnplMessagingCurrency(code)) return null;

  return (
    <BnplPanel
      amountMinor={amountMinor}
      currency={code}
      className={className}
    />
  );
}
